// İSG uzmanı portalı — Firma Onay-Routing ekle/düzenle formu (api-contract §31.2). Belge türü seç
// (yeni'de; düzenle'de kimliktir — sabit) + **departman çoklu seçim** (İSG/Hekim/İK; bir veya çok) +
// **eskalasyon süresi** (saat) + **eskale departmanı**. Eskalasyon **hem-ya-hiç**: süre girildiyse
// departman zorunlu (ve tersi). Belge türü kimliği = `DocumentTypeRef` (`entity`, `code`); seçenekler
// §27 kaydının AKTİF personel + araç türleri, düz liste "Personel belgesi · X" (önce personel). Düzenle'de
// tür sabittir; kayıtta pasifse "(pasif)" eki alır (DR 10). Yeni satırda `code` reddedilirse
// (`INVALID`/`INACTIVE`, §27.6) kayıt tazelenir, seçim temizlenir, alan hatası gösterilir. Yazma yetkisi
// backend'de (`permit-types` grant); yetkisiz → 403 hata olarak yansır.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  type DocumentTypeRef,
  Field,
  Input,
  Modal,
  PERMIT_APPROVAL_DEPARTMENTS,
  type PermitApprovalDepartment,
  type PermitApprovalRoutingDto,
  REQUIREMENT_ENTITIES,
  Select,
  type SelectOption,
  Spinner,
  documentTypeRefKey,
  isTypeFieldRejection,
  permitApprovalRoutingApi,
  resolveApiErrorMessage,
  useDocumentTypes,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { permitApprovalRoutingKeys } from '../../lib/permitApprovalRoutingKeys';
import { DocumentTypeField } from '../documents/DocumentTypeField';

interface ApprovalRoutingFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Düzenlenecek routing — verilmezse oluşturma kipi. */
  routing?: PermitApprovalRoutingDto | null;
  /** Mevcut routing'ler — oluşturma kipinde zaten yapılandırılmış türleri seçenekten çıkarmak için. */
  existing?: PermitApprovalRoutingDto[];
  onSuccess: (message: string) => void;
}

/**
 * Tek Select değeri `documentTypeRefKey` (`"personnel:<code>"`) → `(entity, code)`; kod `:`
 * içeremez (§27.0(2)). Seçenek sırası `REQUIREMENT_ENTITIES` (önce personel; `company` yok, #19).
 */
function decodeRef(key: string): DocumentTypeRef | null {
  const sep = key.indexOf(':');
  const entity = key.slice(0, sep);
  const code = key.slice(sep + 1);
  return (entity === 'personnel' || entity === 'vehicle') && code.length > 0
    ? { entity, code }
    : null;
}

export function ApprovalRoutingFormModal({
  open,
  onOpenChange,
  routing,
  existing = [],
  onSuccess,
}: ApprovalRoutingFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const registry = useDocumentTypes();
  const isEdit = Boolean(routing);

  const schema = useMemo(
    () =>
      z
        .object({
          documentTypeKey: z
            .string()
            .trim()
            .refine((key) => decodeRef(key) !== null, t('approvalRouting.form.error.documentType')),
          requiredDepartments: z
            .array(z.enum(['Isg', 'Hekim', 'Ik']))
            .min(1, t('approvalRouting.form.error.departments')),
          escalationHours: z.string().trim(),
          escalationDepartment: z.string().trim(),
        })
        .superRefine((val, ctx) => {
          const hasHours = val.escalationHours.length > 0;
          const hasDept = val.escalationDepartment.length > 0;
          if (hasHours) {
            const n = Number(val.escalationHours);
            if (!Number.isInteger(n) || n <= 0) {
              ctx.addIssue({
                path: ['escalationHours'],
                code: z.ZodIssueCode.custom,
                message: t('approvalRouting.form.error.hoursPositive'),
              });
            }
            if (!hasDept) {
              ctx.addIssue({
                path: ['escalationDepartment'],
                code: z.ZodIssueCode.custom,
                message: t('approvalRouting.form.error.escalationDeptRequired'),
              });
            }
          }
          if (hasDept && !hasHours) {
            ctx.addIssue({
              path: ['escalationHours'],
              code: z.ZodIssueCode.custom,
              message: t('approvalRouting.form.error.hoursRequired'),
            });
          }
        }),
    [t],
  );
  type FormValues = z.input<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    setFocus,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      documentTypeKey: '',
      requiredDepartments: [],
      escalationHours: '',
      escalationDepartment: '',
    },
  });

  // Modal açılışında (veya düzenlenen kayıt değişince) formu doldur/temizle.
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız open/routing tetiklemeli
  useEffect(() => {
    if (!open) {
      return;
    }
    reset({
      documentTypeKey: routing ? documentTypeRefKey(routing) : '',
      requiredDepartments: routing?.requiredDepartments ?? [],
      escalationHours: routing?.escalationHours != null ? String(routing.escalationHours) : '',
      escalationDepartment: routing?.escalationDepartment ?? '',
    });
  }, [open, routing]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const hasHours = values.escalationHours.trim().length > 0;
      const departments = values.requiredDepartments as PermitApprovalDepartment[];
      const escalationHours = hasHours ? Number(values.escalationHours) : null;
      const escalationDepartment = hasHours
        ? (values.escalationDepartment as PermitApprovalDepartment)
        : null;
      if (routing) {
        return permitApprovalRoutingApi.updatePermitApprovalRouting(routing.uuid, {
          requiredDepartments: departments,
          escalationHours,
          escalationDepartment,
        });
      }
      const ref = decodeRef(values.documentTypeKey);
      if (ref === null) {
        // Şema bunu zaten reddeder; yalnız tip daraltması için.
        throw new Error('documentTypeKey');
      }
      return permitApprovalRoutingApi.createPermitApprovalRouting({
        entity: ref.entity,
        code: ref.code,
        requiredDepartments: departments,
        escalationHours,
        escalationDepartment,
      });
    },
    // §27.6: kayıt tazelenir, seçim temizlenir, alan hatası + odak; aynı gövde yeniden gönderilmez.
    // Yalnız oluşturma: `PUT` gövdesi tür taşımaz.
    onError: (error) => {
      if (isEdit || !isTypeFieldRejection(error)) {
        return;
      }
      void registry.refetch();
      setValue('documentTypeKey', '');
      setError('documentTypeKey', { type: 'server', message: t('documentTypes.typeRejected') });
      setFocus('documentTypeKey');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitApprovalRoutingKeys.all() });
      onSuccess(isEdit ? t('approvalRouting.form.updated') : t('approvalRouting.form.created'));
      onOpenChange(false);
    },
  });

  const departmentOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('approvalRouting.form.noEscalationDept') },
      ...PERMIT_APPROVAL_DEPARTMENTS.map((d) => ({
        value: d,
        label: t(`permits.department.${d}`),
      })),
    ],
    [t],
  );

  /** "Personel belgesi · Sağlık raporu" — kayıt etiketi (yüklenmemişse ham kod). */
  function refLabel(ref: DocumentTypeRef): string {
    return `${t(`documentSchemas.entity.${ref.entity}`)} · ${registry.label(ref.entity, ref.code)}`;
  }

  // Belge türü seçenekleri (yalnız oluşturma): kaydın aktif personel + araç türleri. Zaten routing
  // taşıyan `(entity, code)` çiftleri çıkarılır (upsert karışıklığı olmasın).
  const configured = new Set(existing.map(documentTypeRefKey));
  const activeOptions: SelectOption[] = REQUIREMENT_ENTITIES.flatMap((entity) =>
    registry.selectable(entity).map((type) => {
      const ref = { entity, code: type.code };
      return { value: documentTypeRefKey(ref), label: refLabel(ref) };
    }),
  );
  const documentTypeOptions = activeOptions.filter((o) => !configured.has(o.value));
  const allConfigured = activeOptions.length > 0 && documentTypeOptions.length === 0;

  // Düzenlenen türün okunur etiketi (sabit alan); kayıtta pasif ya da yoksa "(pasif)".
  const editDocLabel = routing ? refLabel(routing) : '';
  const editDocInactive =
    routing != null &&
    registry.types !== undefined &&
    registry.find(routing.entity, routing.code)?.isActive !== true;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('approvalRouting.form.edit.title') : t('approvalRouting.form.create.title')}
      description={t('approvalRouting.form.description')}
      size="md"
      closeLabel={t('permitTypes.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permitTypes.common.cancel')}
          </Button>
          <Button type="submit" form="approval-routing-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('permitTypes.common.save')} />
            ) : (
              t('permitTypes.common.save')
            )}
          </Button>
        </>
      }
    >
      <form
        id="approval-routing-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {/* Tür reddi alanın altında gösterilir; genel Alert onu yinelemez. */}
        {mutation.isError && (isEdit || !isTypeFieldRejection(mutation.error)) ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {/* Belge türü — düzenle'de sabit (kimlik); yeni'de seçicidir. */}
        {isEdit ? (
          <Field
            htmlFor="approval-routing-document-type-fixed"
            label={t('approvalRouting.form.field.documentType')}
          >
            <p
              id="approval-routing-document-type-fixed"
              className="rounded-md border border-border bg-surface-muted px-3 py-2 text-sm text-fg"
            >
              {editDocLabel}
              {editDocInactive ? (
                <span className="ml-1 text-xs text-muted">
                  {t('permitTypes.catalog.inactiveSuffix')}
                </span>
              ) : null}
            </p>
          </Field>
        ) : (
          <DocumentTypeField
            id="approval-routing-document-type"
            label={t('approvalRouting.form.field.documentType')}
            options={documentTypeOptions}
            emptyHint={allConfigured ? t('approvalRouting.form.allConfigured') : undefined}
            error={errors.documentTypeKey?.message}
            registration={register('documentTypeKey')}
          />
        )}

        {/* Departman çoklu seçim (İSG/Hekim/İK; bir veya çok). */}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-fg">
            {t('approvalRouting.form.field.departments')}
            <span className="ml-0.5 text-danger">*</span>
          </legend>
          <p className="text-xs text-muted">{t('approvalRouting.form.departmentsHint')}</p>
          <div className="flex flex-col gap-2">
            {PERMIT_APPROVAL_DEPARTMENTS.map((d) => (
              <label
                key={d}
                htmlFor={`approval-routing-dept-${d}`}
                className="flex items-center gap-2 text-sm text-fg"
              >
                <input
                  id={`approval-routing-dept-${d}`}
                  type="checkbox"
                  value={d}
                  className="h-4 w-4 rounded border-border-strong text-primary focus-visible:ring-2 focus-visible:ring-ring"
                  {...register('requiredDepartments')}
                />
                {t(`permits.department.${d}`)}
              </label>
            ))}
          </div>
          {errors.requiredDepartments ? (
            <p role="alert" className="text-xs font-medium text-danger">
              {errors.requiredDepartments.message}
            </p>
          ) : null}
        </fieldset>

        {/* Eskalasyon (hem-ya-hiç). */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            htmlFor="approval-routing-escalation-hours"
            label={t('approvalRouting.form.field.escalationHours')}
            error={errors.escalationHours?.message}
            hint={t('approvalRouting.form.escalationHint')}
          >
            <Input
              id="approval-routing-escalation-hours"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              invalid={Boolean(errors.escalationHours)}
              placeholder={t('approvalRouting.form.escalationHoursPlaceholder')}
              {...register('escalationHours')}
            />
          </Field>
          <Field
            htmlFor="approval-routing-escalation-dept"
            label={t('approvalRouting.form.field.escalationDepartment')}
            error={errors.escalationDepartment?.message}
          >
            <Select
              id="approval-routing-escalation-dept"
              options={departmentOptions}
              invalid={Boolean(errors.escalationDepartment)}
              {...register('escalationDepartment')}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
